import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useSession } from '../hooks/useSession'
import DataEditor, { GridCellKind, type CustomRenderer, type EditableGridCell, type Item } from '@glideapps/glide-data-grid'
import { DropdownCell, type DropdownCellType } from '@glideapps/glide-data-grid-cells'
import { EditablePill } from '../components/EditablePill'

// SEM-68: wrap the library DropdownCell so the closed cell shows a chevron.
// Without this the cell renders as plain text and there's no affordance that
// it's a dropdown — only `allowOverlay: true` editable cells get the chevron.
const DropdownCellWithChevron: CustomRenderer<DropdownCellType> = {
  ...DropdownCell,
  draw: (args, cell) => {
    DropdownCell.draw(args, cell)
    if (!cell.allowOverlay) return
    const { ctx, rect, theme } = args
    ctx.save()
    ctx.fillStyle = theme.textLight
    ctx.font = `10px ${theme.fontFamily}`
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.fillText('▾', rect.x + rect.width - 8, rect.y + rect.height / 2 + 1)
    ctx.restore()
  },
}
import '@glideapps/glide-data-grid/dist/index.css'
import { classifyColumns } from '../lib/classifyColumns'
import { useGridState } from '../lib/useGridState'
import { relativeTime } from '../lib/relativeTime'
import { useGridData } from '../hooks/useGridData'
import { wsStore, loadWorkspace } from '../lib/workspaceStore'
import MacroCharts from '../components/MacroCharts'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { Logo } from '../components/prototype-ui'
import { gridTheme, gridThemeLight } from '../components/grid/theme'
import { ProcessingView } from '../components/grid/ProcessingView'
import NotFound from './NotFound'
import { MeshPanel } from '../components/grid/MeshPanel'
import { RowInspector } from '../components/grid/RowInspector'
import { TimeMachineModal } from '../components/grid/TimeMachineModal'
import { SignInPanel } from '../components/auth/SignInPanel'

type Visibility = 'public' | 'private'

// Mirrors worker `ActionColumn` (SEM-68).
type ActionColumn = {
  column: string
  type: 'checkbox' | 'select' | 'text'
  default?: unknown
  options?: string[]
}

interface MeshMeta {
  handle: string
  slug: string
  version: number
  visibility: Visibility
  rows: number
  updated_at: string
  title: string
  description: string | null
  tags: string[]
  status: string
  allow_embed?: boolean
  // SEM-67: agent-declared per-grid lock; undefined or true = editable.
  editable?: boolean
  // SEM-68: when present, only these columns are editable (typed controls).
  actions?: ActionColumn[] | null
  copied_from?: string | null
}

// Query-param marker the auth callback returns with so the copy can resume
// post-sign-in. Promoted to a constant so the protocol is grep-able.
const COPY_REPLAY_PARAM = 'copy'

export default function MeshGrid() {
  const navigate = useNavigate()
  const { handle, slug } = useParams<{ handle: string; slug: string }>()
  const [searchParams] = useSearchParams()
  const isEmbed       = searchParams.get('embed')     === 'true'
  const isLight       = searchParams.get('theme')     === 'light'
  const isCompact     = searchParams.get('compact')   === 'true'
  const showInspector = searchParams.get('inspector') === 'true'
  const isReadonly    = searchParams.get('readonly')  === 'true'
  const sortParam     = searchParams.get('sort') ?? ''
  const activeTheme   = isLight ? gridThemeLight : gridTheme

  const { status: sessionStatus } = useSession()
  const ws = wsStore.use()
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('instadash_key') ?? '')
  const [keyInput, setKeyInput] = useState('')

  // Load workspace once a session is known so we can derive ownership + plan
  useEffect(() => {
    if (sessionStatus === 'signed-in' && ws.status === 'idle') loadWorkspace()
  }, [sessionStatus, ws.status])

  const isOwner = !!ws.workspace?.handle && ws.workspace.handle === handle

  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const [error, setError] = useState<string | null>(null)
  const [meta, setMeta] = useState<MeshMeta | null>(null)

  // HITL edit gate: owner + paid plan + per-link `?readonly=true` opt-out
  // + SEM-67 grid-level lock (`meta.editable === false`). Server enforces too.
  const canEdit = isOwner && ws.plan !== null && ws.plan !== 'free' && !isReadonly && meta?.editable !== false
  const [retryCount, setRetryCount] = useState(0)
  const [copied, setCopied] = useState(false)
  type CopyState =
    | { kind: 'idle' }
    | { kind: 'copying' }
    | { kind: 'done'; url: string; handle: string; slug: string }
    | { kind: 'error'; message: string }
  const [copyState, setCopyState] = useState<CopyState>({ kind: 'idle' })
  const [showSignIn, setShowSignIn] = useState(false)
  const [magicLinkSentTo, setMagicLinkSentTo] = useState<string | null>(null)

  const [search, setSearch] = useState(() => searchParams.get('search') ?? '')
  const [showCharts, setShowCharts] = useState(true)
  const [showMesh, setShowMesh] = useState(false)
  const [tmOpen, setTmOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [lockBusy, setLockBusy] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close kebab on outside click. Cheaper than a portal — the menu is anchored.
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const toggleEditableLock = useCallback(async () => {
    if (!slug || !meta || lockBusy) return
    const next = meta.editable === false
    setLockBusy(true)
    try {
      const r = await fetch(`/api/grids/${slug}/editable`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ editable: next }),
      })
      if (r.ok) {
        setMeta({ ...meta, editable: next })
        setMenuOpen(false)
      }
    } finally {
      setLockBusy(false)
    }
  }, [slug, meta, lockBusy])

  const stateKey = `mesh-${handle ?? ''}-${slug ?? ''}`
  const { sortRules, freezeCount, colWidths, colOrder, toggleSort, setColWidth, setColOrder } =
    useGridState(stateKey)

  function parseSortParam(raw: string): Array<{ col: string; dir: 'asc' | 'desc' }> {
    return raw.split(',').flatMap(s => {
      const [col, dir] = s.split(':')
      return col && (dir === 'asc' || dir === 'desc') ? [{ col, dir }] : []
    })
  }
  const effectiveSortRules = sortRules.length === 0 && sortParam
    ? parseSortParam(sortParam)
    : sortRules

  const fetchPage = useCallback(
    async (offset: number, limit: number) => {
      if (!handle || !slug) throw new Error('Missing handle or slug')
      const r = await fetch(`/${handle}/${slug}/rows?offset=${offset}&limit=${limit}`, {
        credentials: 'include',
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ rows: Record<string, unknown>[]; total: number }>
    },
    [handle, slug],
  )

  const grid = useGridData({
    totalRows: meta?.rows ?? 0,
    fetchPage,
    sortRules: effectiveSortRules,
    colOrder,
    colWidths,
    search,
    canEdit,
    actions: meta?.actions ?? null,
  })

  // Debounced PATCH for edit persistence (HITL, SEM-52). Optimistic UI is in useGridData.
  const pendingEdits = useRef<{ row: number; col: string; value: unknown }[]>([])
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushEdits = useCallback(async () => {
    flushTimer.current = null
    const batch = pendingEdits.current.splice(0)
    if (!batch.length || !slug) return
    try {
      await fetch(`/api/grids/${slug}/cells`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits: batch }),
      })
    } catch {
      // Edits remain in the local overlay; user can retry on next edit.
    }
  }, [slug])
  const queueEdit = useCallback((row: number, col: string, value: unknown) => {
    pendingEdits.current.push({ row, col, value })
    if (flushTimer.current) clearTimeout(flushTimer.current)
    flushTimer.current = setTimeout(() => { void flushEdits() }, 400)
  }, [flushEdits])

  const chartSpecs = useMemo(
    () => (grid.fullyLoaded ? classifyColumns(grid.schema, grid.rows) : []),
    [grid.schema, grid.rows, grid.fullyLoaded],
  )

  const getRowThemeOverride = useCallback((row: number) => {
    if (row === selectedRow || row === hoveredRow) return { bgCell: isLight ? '#DCF0F3' : '#173B43' }
    return row % 2 === 1 ? { bgCell: isLight ? '#F5F6F7' : '#1F242A' } : undefined
  }, [hoveredRow, selectedRow, isLight])

  // ── Initial meta fetch ─────────────────────────────────────────────────────
  const fetchMeta = useCallback(() => {
    if (!handle || !slug) return
    setError(null)
    fetch(`/${handle}/${slug}/meta`, { credentials: 'include' })
      .then(async r => {
        if (r.status === 202) {
          // Sentinel says workflow is still running — retry
          setRetryCount(n => n + 1)
          return null
        }
        if (r.status === 404) {
          const body = await r.json().catch(() => ({})) as { tombstone?: boolean }
          if (body.tombstone) {
            // Private grid — needs auth regardless of session state
            throw new Error('AUTH')
          }
          // Genuinely doesn't exist or was deleted
          throw new Error('NOT_FOUND')
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<MeshMeta>
      })
      .then(m => {
        if (m === null) return
        setMeta(m)
      })
      .catch((e: Error) => setError(e.message))
  }, [handle, slug])

  useEffect(() => { fetchMeta() }, [fetchMeta])

  useEffect(() => {
    if (retryCount === 0) return
    const t = setTimeout(fetchMeta, 2500)
    return () => clearTimeout(t)
  }, [retryCount, fetchMeta])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setSize({ width: el.offsetWidth, height: el.offsetHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [meta, showMesh, selectedRow])

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleCopyGrid = useCallback(async () => {
    if (!handle || !slug) return
    if (sessionStatus !== 'signed-in') {
      setShowSignIn(true)
      return
    }
    setCopyState({ kind: 'copying' })
    try {
      const r = await fetch(`/grids/${handle}/${slug}/copy`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      const body = await r.json().catch(() => ({})) as { handle?: string; slug?: string; grid_url?: string; error?: string }
      if (!r.ok || !body.grid_url || !body.handle || !body.slug) {
        setCopyState({ kind: 'error', message: body.error ?? `Copy failed (HTTP ${r.status})` })
        return
      }
      setCopyState({ kind: 'done', url: body.grid_url, handle: body.handle, slug: body.slug })
      // Strip the replay marker so a refresh doesn't re-fork.
      const next = new URL(window.location.href)
      if (next.searchParams.has(COPY_REPLAY_PARAM)) {
        next.searchParams.delete(COPY_REPLAY_PARAM)
        window.history.replaceState({}, '', next.toString())
      }
    } catch (e) {
      setCopyState({ kind: 'error', message: e instanceof Error ? e.message : 'Network error' })
    }
  }, [handle, slug, sessionStatus])

  // Resume the copy after the sign-in callback returns with ?copy=1.
  useEffect(() => {
    if (searchParams.get(COPY_REPLAY_PARAM) !== '1') return
    if (sessionStatus !== 'signed-in') return
    if (copyState.kind !== 'idle') return
    handleCopyGrid()
  }, [searchParams, sessionStatus, copyState.kind, handleCopyGrid])

  function submitKey(e: React.FormEvent) {
    e.preventDefault()
    const k = keyInput.trim()
    if (!k) return
    localStorage.setItem('instadash_key', k)
    setApiKey(k)
  }

  function handleExportCsv() {
    if (!grid.fullyLoaded) return
    const cols = grid.schema.map(s => s.key)
    const escape = (v: unknown) => {
      const s = String(v ?? '')
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const lines = [cols.join(','), ...grid.rows.map(r => cols.map(c => escape(r[c])).join(','))]
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${meta?.handle}-${meta?.slug}-v${meta?.version}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!handle || !slug) {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <p className="muted">Missing handle or slug.</p>
      </div>
    )
  }

  if (error === 'AUTH') {
    return (
      <div className="container" style={{ padding: '80px 0', maxWidth: 480 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>private grid</div>
        <h1 className="h1">Sign in to view.</h1>
        <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
          <code className="mono">{handle}/{slug}</code> is private or doesn&apos;t exist. Paste your API key to continue.
        </p>
        <form onSubmit={submitKey} className="row gap-8">
          <input
            autoFocus type="text" placeholder="sk_…"
            value={keyInput} onChange={e => setKeyInput(e.target.value)}
            className="mono"
            style={{
              flex: 1, background: 'var(--bg-1)', border: '1px solid var(--line)',
              borderRadius: 6, padding: '10px 14px', color: 'var(--fg)', fontSize: 14, outline: 'none',
            }}
          />
          <button type="submit" className="btn btn-primary">Open →</button>
        </form>
      </div>
    )
  }

  if (error === 'NOT_FOUND') {
    return <NotFound />
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '80px 0', maxWidth: 480 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>{handle}/{slug}</div>
        <h1 className="h1">Failed to load.</h1>
        <p className="lede" style={{ marginTop: 14, color: 'var(--fg-2)', fontSize: 15 }}>
          Something went wrong loading this grid.
        </p>
        <code className="mono" style={{ display: 'block', marginTop: 12, fontSize: 12, color: 'var(--fg-3)', padding: '10px 14px', background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6 }}>{error}</code>
        <div className="row gap-12" style={{ marginTop: 28 }}>
          <button className="btn btn-primary" onClick={fetchMeta}>Retry</button>
          <button className="btn btn-ghost" onClick={() => navigate('/workspace')}>← Your grids</button>
        </div>
      </div>
    )
  }

  if (retryCount > 0 && !meta) {
    return <ProcessingView handle={handle} slug={slug} />
  }

  if (!meta) {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <p className="muted mono" style={{ fontSize: 13 }}>Loading…</p>
      </div>
    )
  }

  // ── Embed gating ───────────────────────────────────────────────────────────
  if (isEmbed && meta.allow_embed === false) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0D1117', flexDirection: 'column', gap: 16 }}>
        <p className="mono" style={{ color: 'var(--fg-2)', fontSize: 14, margin: 0 }}>Embeds require the Builder plan.</p>
        <a href="/pricing" style={{ color: 'var(--lime)', fontFamily: 'var(--mono)', fontSize: 13, textDecoration: 'none', border: '1px solid var(--lime)', borderRadius: 6, padding: '6px 16px' }}>Upgrade →</a>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: isLight ? '#FFFFFF' : undefined }}>
      {/* Grid header */}
      {!isEmbed && <div className="grid-page-header" style={{ padding: '14px 24px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div className="row gap-8" style={{ flex: '1 1 auto', minWidth: 0, flexWrap: 'wrap' }}>
          <Logo size={14} onClick={() => navigate('/')} />
          <button className="btn btn-ghost btn-mono" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => navigate('/workspace')}>← grids</button>
          <span className="mono hide-mobile" style={{ fontSize: 12, color: 'var(--fg-3)' }}>/</span>
          <span style={{ fontWeight: 600, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{meta.title}</span>
          <span className="mono hide-mobile" style={{ fontSize: 11, color: 'var(--fg-3)' }}>· {meta.handle}/{meta.slug}</span>
          {meta.visibility === 'public' ? (
            <>
              <span className="chip chip-lime hide-mobile" style={{ fontSize: 10 }}><span className="dot"></span>live</span>
              <span className="chip hide-mobile" style={{ fontSize: 10 }}><span className="dot"></span>public</span>
            </>
          ) : (
            <span className="chip" style={{ fontSize: 10 }}><span className="dot"></span>private</span>
          )}
          <span className="hide-mobile"><EditablePill editable={meta.editable} /></span>
        </div>
        <span className="spacer"></span>
        <div className="row gap-8">
          {/* Mesh toggle hidden until launch — beta feature, re-enable post-launch */}
          {/* {meta.visibility === 'public' && (
            <button className="btn btn-ghost btn-mono" style={{ fontSize: 12 }}
              onClick={() => { setShowMesh(!showMesh); if (!showMesh) setShowCharts(false) }}>
              {showMesh ? '✕ mesh' : '◇ mesh'}
            </button>
          )} */}
          <button className="btn btn-ghost btn-mono" style={{ fontSize: 12 }} onClick={() => setTmOpen(true)}>v{meta.version} ↻</button>
          <button className="btn btn-ghost btn-mono" style={{ fontSize: 12 }} onClick={handleCopyLink}>{copied ? '✓ link copied' : 'share'}</button>
          {copyState.kind === 'done' ? (
            <a
              className="btn btn-ghost btn-mono"
              style={{ fontSize: 12, color: 'var(--lime)', textDecoration: 'none' }}
              href={`/${copyState.handle}/${copyState.slug}`}
              title={`Open your copy: ${copyState.handle}/${copyState.slug}`}
            >✓ open copy →</a>
          ) : (
            <button
              className="btn btn-ghost btn-mono"
              style={{ fontSize: 12, ...(copyState.kind === 'error' ? { color: 'var(--err, #ff6b6b)' } : {}) }}
              onClick={handleCopyGrid}
              disabled={copyState.kind === 'copying'}
              title={copyState.kind === 'error' ? copyState.message : 'Fork this grid into your account'}
            >
              {copyState.kind === 'copying' ? 'copying…'
                : copyState.kind === 'error' ? '× retry copy'
                : '+ copy'}
            </button>
          )}
          <button className="btn btn-ghost btn-mono" style={{ fontSize: 12 }} onClick={handleExportCsv} disabled={!grid.fullyLoaded}>export</button>
          {isOwner && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                className="btn btn-ghost btn-mono"
                style={{ fontSize: 14, padding: '4px 9px' }}
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Grid actions"
                aria-expanded={menuOpen}
              >⋯</button>
              {menuOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                    minWidth: 200, padding: 4,
                    background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                    borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                  }}
                >
                  <button
                    role="menuitem"
                    onClick={toggleEditableLock}
                    disabled={lockBusy}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 10px', fontSize: 12, fontFamily: 'var(--mono)',
                      background: 'transparent', border: 'none', borderRadius: 4,
                      color: 'var(--fg-1)', cursor: lockBusy ? 'wait' : 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {lockBusy ? '…' : meta.editable === false ? '✎ unlock grid (allow edits)' : '🔒 lock grid (block edits)'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* Description + tags + lineage strip (if any present) */}
      {!isEmbed && (meta.description || meta.tags.length > 0 || meta.copied_from) && (
        <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {meta.description && <span style={{ color: 'var(--fg-2)', fontSize: 12 }}>{meta.description}</span>}
          {meta.tags.length > 0 && (
            <div className="row gap-4" style={{ flexWrap: 'wrap' }}>
              {meta.tags.map(tag => (
                <span key={tag} style={{ background: 'var(--bg-2)', border: '1px solid var(--line-soft)', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {meta.copied_from && (
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
              copied from{' '}
              <a href={`/${meta.copied_from}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                {meta.copied_from}
              </a>
            </span>
          )}
        </div>
      )}

      {/* Toolbar */}
      {!isEmbed && <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-1)', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280, position: 'relative', maxWidth: 460 }}>
          <span className="mono" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--accent)', fontSize: 12 }}>⌕</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={grid.fullyLoaded ? 'filter rows…' : `loading ${grid.loadPct}% — search after rows load`}
            disabled={!grid.fullyLoaded}
            className="mono"
            style={{
              width: '100%', padding: '7px 12px 7px 28px', fontSize: 12,
              background: 'var(--bg)', border: '1px solid var(--line)',
              borderRadius: 5, color: 'var(--fg)', outline: 'none', opacity: grid.fullyLoaded ? 1 : 0.6,
            }}
          />
        </div>
        {/* Charts toggle hidden until launch — beta feature, re-enable post-launch */}
        {/* <button onClick={() => setShowCharts(!showCharts)} className="btn btn-ghost btn-mono" style={{ fontSize: 11 }}>
          {showCharts ? '▼ charts' : '▶ charts'}
        </button> */}
        {sortRules.length > 0 && (
          <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>
            sorted: {sortRules.map(r => `${r.col} ${r.dir}`).join(', ')}
          </span>
        )}
      </div>}

      {/* Macro charts hidden until launch — beta feature, re-enable post-launch */}
      {/* {!isEmbed && showCharts && !showMesh && grid.fullyLoaded && chartSpecs.length > 0 && (
        <MacroCharts specs={chartSpecs} rows={grid.rows} />
      )} */}

      {/* Main: grid + side panels */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div ref={containerRef} style={{ flex: 1, overflow: 'hidden' }}>
          {size.width > 0 && grid.schema.length > 0 && (
            <ErrorBoundary>
              <DataEditor
                ref={grid.gridRef}
                getCellContent={grid.getCellContent}
                columns={grid.columns}
                rows={grid.rowCount}
                width={size.width}
                height={size.height}
                rowMarkers="number"
                smoothScrollX
                smoothScrollY
                rowHeight={isCompact ? 26 : 34}
                theme={isCompact ? { ...activeTheme, cellVerticalPadding: 3 } : activeTheme}
                freezeColumns={freezeCount}
                onVisibleRegionChanged={grid.onVisibleRegionChanged}
                onCellClicked={(cell) => setSelectedRow(cell[1])}
                customRenderers={[DropdownCellWithChevron]}
                onCellEdited={canEdit ? (cell: Item, newValue: EditableGridCell) => {
                  const [colIdx, displayRow] = cell
                  const row = grid.getRawRow(displayRow)
                  const rawRowIdx = row?.__rowIdx
                  const colKey = grid.columns[colIdx]?.id
                  if (rawRowIdx === undefined || !colKey) return
                  // SEM-68: Custom cells (e.g. dropdown) wrap the real value inside `data.value`.
                  let value: unknown
                  if (newValue.kind === GridCellKind.Custom) {
                    const inner = newValue.data as { kind?: string; value?: unknown } | undefined
                    value = inner?.kind === 'dropdown-cell' ? inner.value ?? null : null
                  } else {
                    value = 'data' in newValue ? newValue.data : null
                  }
                  grid.applyEdit(rawRowIdx, colKey, value)
                  queueEdit(rawRowIdx, colKey, value)
                } : undefined}
                onHeaderClicked={(col, event) => {
                  if (!grid.fullyLoaded) return
                  const colId = grid.columns[col]?.id
                  if (colId) toggleSort(colId, (event as unknown as MouseEvent).shiftKey)
                }}
                onColumnResize={(_col, newSize, colIndex) => {
                  const colId = grid.columns[colIndex]?.id
                  if (colId) setColWidth(colId, newSize)
                }}
                onColumnMoved={(startIndex, endIndex) => {
                  const ids = grid.columns.map(c => c.id ?? '')
                  const reordered = [...ids]
                  const [removed] = reordered.splice(startIndex, 1)
                  reordered.splice(endIndex, 0, removed)
                  setColOrder(reordered)
                }}
                getRowThemeOverride={getRowThemeOverride}
                onItemHovered={(args) => setHoveredRow(args.kind === 'cell' ? args.location[1] : null)}
              />
            </ErrorBoundary>
          )}
        </div>

        {/* Mesh panel hidden until launch — beta feature, re-enable post-launch */}
        {/* {showMesh && <MeshPanel onClose={() => setShowMesh(false)} />} */}
        {(!isEmbed || showInspector) && selectedRow !== null && (() => {
          const row = grid.getRow(selectedRow)
          return row ? <RowInspector row={row} onClose={() => setSelectedRow(null)} /> : null
        })()}
      </div>

      {/* Status bar */}
      {!isEmbed && <div style={{
        padding: '6px 24px', borderTop: '1px solid var(--line-soft)',
        display: 'flex', alignItems: 'center', gap: 18,
        fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--fg-3)', background: 'var(--bg-1)',
      }}>
        <span>{grid.rowCount.toLocaleString()} rows{search && grid.fullyLoaded && ` of ${meta.rows.toLocaleString()}`}</span>
        <span>·</span>
        <span>{grid.columns.length} cols</span>
        {!grid.fullyLoaded && (
          <>
            <span>·</span>
            <span style={{ color: 'var(--accent)' }}>loading {grid.loadPct}%</span>
          </>
        )}
        {freezeCount > 0 && (
          <>
            <span>·</span>
            <span style={{ color: 'var(--accent)' }}>{freezeCount} frozen</span>
          </>
        )}
        <span className="spacer"></span>
        <span>v{meta.version}</span>
        {!isEmbed && <>
          <span>·</span>
          <span>r2://instadash/grids/{meta.handle}/{meta.slug}/v{meta.version}.jsonl</span>
          <span>·</span>
          <span style={{ color: 'var(--lime)' }}>● {relativeTime(meta.updated_at, { terse: true })}</span>
          {meta.visibility === 'public' && (
            <>
              <span>·</span>
              <a href={`/${meta.handle}/${meta.slug}/llms.md`} target="_blank" rel="noreferrer" style={{ color: 'var(--fg-3)' }}>llms.md</a>
              <a href={`/${meta.handle}/${meta.slug}/llms.txt`} target="_blank" rel="noreferrer" style={{ color: 'var(--fg-3)' }}>llms.txt</a>
            </>
          )}
        </>}
      </div>}

      {tmOpen && meta && (
        <TimeMachineModal
          handle={meta.handle} slug={meta.slug}
          currentVersion={meta.version}
          onClose={() => setTmOpen(false)}
        />
      )}

      {showSignIn && (
        <div
          onClick={() => { setShowSignIn(false); setMagicLinkSentTo(null) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 420, width: '100%',
              background: 'var(--bg-1)', border: '1px solid var(--line)',
              borderRadius: 8, padding: 28,
            }}
          >
            {magicLinkSentTo ? (
              <>
                <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>check your email</div>
                <h2 className="h2" style={{ fontSize: 22 }}>Link sent.</h2>
                <p className="lede" style={{ marginTop: 12, marginBottom: 20, fontSize: 14 }}>
                  We emailed a sign-in link to <code className="mono">{magicLinkSentTo}</code>. Open it on this device and we'll fork the grid for you.
                </p>
                <button
                  type="button"
                  className="btn btn-ghost btn-mono"
                  style={{ fontSize: 12 }}
                  onClick={() => { setShowSignIn(false); setMagicLinkSentTo(null) }}
                >close</button>
              </>
            ) : (
              <>
                <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>copy grid</div>
                <h2 className="h2" style={{ fontSize: 22 }}>Sign in to copy.</h2>
                <p className="lede" style={{ marginTop: 12, marginBottom: 24, fontSize: 14 }}>
                  We'll fork <code className="mono">{handle}/{slug}</code> into your workspace as a private grid you can edit.
                </p>
                <SignInPanel
                  callbackURL={`/${handle}/${slug}?${COPY_REPLAY_PARAM}=1`}
                  onMagicLinkSent={(email) => setMagicLinkSentTo(email)}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
